import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import LoadingSpinner from '../../components/LoadingSpinner';
import './styles.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.eternavaults.com';

export default function EmergencyApproval() {
  const { requestId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [request, setRequest] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState('');

  useEffect(() => {
    if (!isAuthenticated) {
      // Redirect to login, then back to this page
      navigate(`/login?returnTo=/emergency-approval/owner/${requestId}`);
      return;
    }
    fetchRequest();
  }, [requestId, isAuthenticated]);

  useEffect(() => {
    if (request && request.expiresAt) {
      const interval = setInterval(() => {
        updateTimeRemaining();
      }, 1000);
      updateTimeRemaining();
      return () => clearInterval(interval);
    }
  }, [request]);

  const fetchRequest = async () => {
    try {
      setLoading(true);
      const token = await getAccessTokenSilently({
        audience: process.env.REACT_APP_AUTH0_AUDIENCE || 'https://api.eternavaults.com'
      });

      const response = await fetch(`${API_BASE_URL}/api/emergency-access/approval/${requestId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch approval request');
      }

      const data = await response.json();
      setRequest(data.request);
    } catch (err) {
      console.error('Error fetching request:', err);
      setError(err.message || 'Failed to load approval request');
    } finally {
      setLoading(false);
    }
  };

  const updateTimeRemaining = () => {
    if (!request || !request.expiresAt) return;
    
    const now = new Date();
    const expires = new Date(request.expiresAt);
    const diff = expires - now;

    if (diff <= 0) {
      setTimeRemaining('Expired');
      return;
    }

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    if (hours > 0) {
      setTimeRemaining(`${hours}h ${minutes}m ${seconds}s`);
    } else if (minutes > 0) {
      setTimeRemaining(`${minutes}m ${seconds}s`);
    } else {
      setTimeRemaining(`${seconds}s`);
    }
  };

  const handleApprove = async () => {
    await handleDecision(true);
  };

  const handleDeny = async () => {
    await handleDecision(false);
  };

  const handleDecision = async (approved) => {
    try {
      setSubmitting(true);
      setError('');
      
      const token = await getAccessTokenSilently({
        audience: process.env.REACT_APP_AUTH0_AUDIENCE || 'https://api.eternavaults.com'
      });

      const response = await fetch(`${API_BASE_URL}/api/emergency-access/approval/${requestId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ approved })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to ${approved ? 'approve' : 'deny'} request`);
      }

      const data = await response.json();
      setSuccess(true);
      setRequest(prev => ({ ...prev, ownerApproved: approved, ownerApprovedAt: new Date() }));
      
      // Redirect after 3 seconds
      setTimeout(() => {
        navigate('/family-continuity-structure');
      }, 3000);
    } catch (err) {
      console.error('Error submitting decision:', err);
      setError(err.message || `Failed to ${approved ? 'approve' : 'deny'} request`);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="emergency-approval-container">
        <LoadingSpinner type="page" message="Loading approval request..." />
      </div>
    );
  }

  if (error && !request) {
    return (
      <div className="emergency-approval-container">
        <div className="approval-error">
          <h2>Error</h2>
          <p>{error}</p>
          <button onClick={() => navigate('/family-continuity-structure')} className="btn-primary">
            Go to Account Management
          </button>
        </div>
      </div>
    );
  }

  if (!request) {
    return (
      <div className="emergency-approval-container">
        <div className="approval-error">
          <h2>Request Not Found</h2>
          <p>The approval request could not be found or has expired.</p>
          <button onClick={() => navigate('/family-continuity-structure')} className="btn-primary">
            Go to Account Management
          </button>
        </div>
      </div>
    );
  }

  const trustLevel = request.trustLevel || 'designated';
  const trustLevelName = {
    immediate: 'Immediate Access',
    family: 'Family Consensus',
    designated: 'Designated Contact',
    legal: 'Legal Access'
  }[trustLevel] || 'Emergency Access';

  const isExpired = request.expiresAt && new Date(request.expiresAt) < new Date();
  const alreadyDecided = request.ownerApproved !== null && request.ownerApproved !== undefined;

  return (
    <div className="emergency-approval-container">
      <div className="approval-card">
        <div className="approval-header">
          <h1>Emergency Access Approval</h1>
          <p className="approval-subtitle">Review and approve or deny this emergency access request</p>
        </div>

        {success && (
          <div className="approval-success">
            <div className="success-icon">✓</div>
            <p>Request {request.ownerApproved ? 'approved' : 'denied'} successfully!</p>
            <p className="redirect-message">Redirecting to account management...</p>
          </div>
        )}

        {error && (
          <div className="approval-error-message">
            <p>{error}</p>
          </div>
        )}

        {!success && (
          <>
            <div className="approval-details">
              <div className="detail-section">
                <h3>Request Information</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Requester:</span>
                    <span className="detail-value">{request.requesterName || 'Unknown'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Trust Level:</span>
                    <span className="detail-value">{trustLevelName}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Requested:</span>
                    <span className="detail-value">
                      {new Date(request.createdAt).toLocaleString()}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Status:</span>
                    <span className={`detail-value status-${request.status}`}>
                      {request.status === 'pending_owner_approval' ? 'Pending Your Approval' : 
                       request.status === 'granted' ? 'Granted' :
                       request.status === 'denied' ? 'Denied' : request.status}
                    </span>
                  </div>
                </div>
              </div>

              {request.metadata?.reason && (
                <div className="detail-section">
                  <h3>Reason for Request</h3>
                  <p className="reason-text">{request.metadata.reason}</p>
                </div>
              )}

              <div className="detail-section">
                <h3>Access Details</h3>
                <div className="access-info">
                  <div className="info-item">
                    <span className="info-icon">⏱️</span>
                    <div>
                      <strong>Time Delay:</strong> {request.delayHours || 48} hours
                    </div>
                  </div>
                  <div className="info-item">
                    <span className="info-icon">👁️</span>
                    <div>
                      <strong>Access Type:</strong> {request.viewOnly ? 'View-only (no downloads/exports)' : 'Full access'}
                    </div>
                  </div>
                  <div className="info-item">
                    <span className="info-icon">⏰</span>
                    <div>
                      <strong>Duration:</strong> {request.maxDuration || 48} hours
                    </div>
                  </div>
                </div>
              </div>

              {timeRemaining && (
                <div className="detail-section">
                  <div className={`time-remaining ${isExpired ? 'expired' : ''}`}>
                    <span className="timer-icon">⏳</span>
                    <div>
                      <strong>Time Remaining:</strong>
                      <span className="timer-value">{timeRemaining}</span>
                    </div>
                    {isExpired && (
                      <p className="expired-message">This request has expired and cannot be approved.</p>
                    )}
                  </div>
                </div>
              )}

              {alreadyDecided && (
                <div className="detail-section">
                  <div className={`decision-made ${request.ownerApproved ? 'approved' : 'denied'}`}>
                    <span className="decision-icon">{request.ownerApproved ? '✓' : '✗'}</span>
                    <div>
                      <strong>Your Decision:</strong>
                      <span className="decision-value">
                        {request.ownerApproved ? 'Approved' : 'Denied'}
                      </span>
                      {request.ownerApprovedAt && (
                        <p className="decision-time">
                          {new Date(request.ownerApprovedAt).toLocaleString()}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {!alreadyDecided && !isExpired && (
              <div className="approval-actions">
                <button
                  onClick={handleApprove}
                  disabled={submitting}
                  className="btn-approve"
                >
                  {submitting ? 'Processing...' : '✓ Approve Request'}
                </button>
                <button
                  onClick={handleDeny}
                  disabled={submitting}
                  className="btn-deny"
                >
                  {submitting ? 'Processing...' : '✗ Deny Request'}
                </button>
              </div>
            )}

            {isExpired && !alreadyDecided && (
              <div className="approval-expired">
                <p>This request has expired and can no longer be approved or denied.</p>
                <button onClick={() => navigate('/family-continuity-structure')} className="btn-primary">
                  Go to Account Management
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

